import type { BruteForcePlayer, PositionCategory, POSITION_CATEGORIES } from '../types';

// Re-export for convenience
const POSITION_TO_CATEGORY: Record<string, PositionCategory> = {
  // Defenders
  LB: 'DEF',
  CB: 'DEF',
  RB: 'DEF',
  // Midfielders (including wing-backs and wide mids)
  LWB: 'MID',
  RWB: 'MID',
  CDM: 'MID',
  CM: 'MID',
  CAM: 'MID',
  LW: 'MID',
  RW: 'MID',
  // Attackers
  ST: 'ATT',
};

/**
 * Get position category (DEF, MID, ATT) from specific position
 */
function getPositionCategory(position: string): PositionCategory | null {
  return POSITION_TO_CATEGORY[position] ?? null;
}

/**
 * Count players in each position category for a team
 */
function countPositionCategories(
  team: BruteForcePlayer[]
): { DEF: number; MID: number; ATT: number } {
  const counts = { DEF: 0, MID: 0, ATT: 0 };

  team.forEach((player) => {
    if (player.primaryPosition) {
      const category = getPositionCategory(player.primaryPosition);
      if (category) {
        counts[category]++;
      }
    }
  });

  return counts;
}

/**
 * Count strikers in a team
 */
function countStrikers(team: BruteForcePlayer[]): number {
  return team.filter((p) => p.primaryPosition === 'ST').length;
}

/**
 * Attack rating of a team's best striker (0 if the team has none)
 */
function leadStrikerAttack(team: BruteForcePlayer[]): number {
  return Math.max(0, ...team.filter((p) => p.primaryPosition === 'ST').map((p) => p.attack));
}

/**
 * Weight of the lead-striker quality gap, on top of the count-based terms.
 *
 * Counting strikers alone can't separate two elite strikers: with three
 * strikers every split is 2v1, so "both best strikers on one side" scored the
 * same as splitting them (WNF #116: Kuba + Tayo vs Paul). 0.3 is past the
 * tipping point measured on #116 (0.1 kept them together, 0.2+ split them).
 * Uses attack rating only — not rater count — so new players aren't discounted.
 */
const LEAD_STRIKER_WEIGHT = 0.3;

/**
 * Calculate the position balance score
 * Measures striker distribution, lead-striker quality and position category coverage.
 * Permanent goalkeepers are excluded — they have no outfield position.
 *
 * Returns: A score where 0 = perfect balance, higher = worse
 */
export function calculatePositionScore(
  blueTeam: BruteForcePlayer[],
  orangeTeam: BruteForcePlayer[]
): number {
  const blueOutfield = blueTeam.filter((p) => !p.isPermanentGK);
  const orangeOutfield = orangeTeam.filter((p) => !p.isPermanentGK);

  // Striker balance - most important (all strikers on one team is bad)
  const blueST = countStrikers(blueOutfield);
  const orangeST = countStrikers(orangeOutfield);
  const stImbalance = Math.abs(blueST - orangeST);

  // Position category coverage
  const blueCounts = countPositionCategories(blueOutfield);
  const orangeCounts = countPositionCategories(orangeOutfield);

  // Calculate coverage imbalance for each category
  const defImbalance = Math.abs(blueCounts.DEF - orangeCounts.DEF);
  const midImbalance = Math.abs(blueCounts.MID - orangeCounts.MID);
  const attImbalance = Math.abs(blueCounts.ATT - orangeCounts.ATT);

  // Penalize if a team lacks players in a category entirely
  const blueMissingCategories =
    (blueCounts.DEF === 0 ? 1 : 0) +
    (blueCounts.MID === 0 ? 1 : 0) +
    (blueCounts.ATT === 0 ? 1 : 0);
  const orangeMissingCategories =
    (orangeCounts.DEF === 0 ? 1 : 0) +
    (orangeCounts.MID === 0 ? 1 : 0) +
    (orangeCounts.ATT === 0 ? 1 : 0);

  // Combine scores with weights
  // ST imbalance is heavily weighted (max ~3 strikers per team in 9v9)
  const stScore = stImbalance / 3;

  // Category imbalance (max ~9 players in a category)
  const categoryScore = (defImbalance + midImbalance + attImbalance) / (3 * 9);

  // Missing category penalty (max 3 categories)
  const missingScore = (blueMissingCategories + orangeMissingCategories) / 6;

  // Lead striker quality: compare each team's best striker (attack is 0-10)
  const leadStrikerScore =
    Math.abs(leadStrikerAttack(blueOutfield) - leadStrikerAttack(orangeOutfield)) / 10;

  // Weight: 60% striker balance, 25% category balance, 15% missing categories,
  // plus the lead-striker quality gap
  return (
    stScore * 0.6 +
    categoryScore * 0.25 +
    missingScore * 0.15 +
    leadStrikerScore * LEAD_STRIKER_WEIGHT
  );
}

/**
 * Get detailed position breakdown for debugging
 */
export function getPositionBreakdown(
  blueTeam: BruteForcePlayer[],
  orangeTeam: BruteForcePlayer[]
): {
  blue: { DEF: number; MID: number; ATT: number; strikers: number; leadStriker: number };
  orange: { DEF: number; MID: number; ATT: number; strikers: number; leadStriker: number };
  gaps: { DEF: number; MID: number; ATT: number; strikers: number; leadStriker: number };
} {
  const blueCounts = countPositionCategories(blueTeam);
  const orangeCounts = countPositionCategories(orangeTeam);
  const blueST = countStrikers(blueTeam);
  const orangeST = countStrikers(orangeTeam);
  const blueLead = leadStrikerAttack(blueTeam.filter((p) => !p.isPermanentGK));
  const orangeLead = leadStrikerAttack(orangeTeam.filter((p) => !p.isPermanentGK));

  return {
    blue: { ...blueCounts, strikers: blueST, leadStriker: blueLead },
    orange: { ...orangeCounts, strikers: orangeST, leadStriker: orangeLead },
    gaps: {
      DEF: Math.abs(blueCounts.DEF - orangeCounts.DEF),
      MID: Math.abs(blueCounts.MID - orangeCounts.MID),
      ATT: Math.abs(blueCounts.ATT - orangeCounts.ATT),
      strikers: Math.abs(blueST - orangeST),
      leadStriker: Math.abs(blueLead - orangeLead),
    },
  };
}
